"use client";

import { useQuery } from "@tanstack/react-query";
import { useParams } from "next/navigation";

import {
  getOrgProfileByName,
  getOrgProfilesByParentId,
} from "@/actions/org-profiles";
import type { OrgProfileModel, WorkModel } from "@/api";
import { useWorkAccessLevels } from "@/hooks/useWorkAccessLevels";
import { useWorkStatuses } from "@/hooks/useWorkStatuses";
import { isValidOrgId } from "@/lib/utils";
import type { Option, WorkFormValues } from "@/schemas/work-form-schema";

// Custom hook to fetch work form data
export function useWorkFormData() {
  const { orgId } = useParams();

  const { data: workTypes, isLoading: isWorkTypesLoading } = useQuery<
    OrgProfileModel[]
  >({
    queryKey: ["workTypes", orgId],
    queryFn: async () => {
      const workTypeKey = await getOrgProfileByName(
        orgId as string,
        "Work Type",
      );
      if (workTypeKey && workTypeKey[0]?.id) {
        const workTypeProfiles = await getOrgProfilesByParentId(
          orgId as string,
          workTypeKey[0].id,
        );
        return workTypeProfiles || [];
      }
      return [];
    },
    enabled: isValidOrgId(orgId as string | undefined),
    // initialData: [],
  });

  const { data: estimateTypes, isLoading: isEstimateTypesLoading } = useQuery<
    OrgProfileModel[]
  >({
    queryKey: ["estimateTypes", orgId],
    queryFn: async () => {
      const estimateTypeKey = await getOrgProfileByName(
        orgId as string,
        "Estimate Type",
      );
      if (estimateTypeKey && estimateTypeKey[0]?.id) {
        const estimateTypeProfiles = await getOrgProfilesByParentId(
          orgId as string,
          estimateTypeKey[0].id,
        );
        return estimateTypeProfiles || [];
      }
      return [];
    },
    enabled: isValidOrgId(orgId as string | undefined),
  });

  const { workStatuses, isLoading: isWorkStatusesLoading } = useWorkStatuses();
  const { workAccessLevels, isLoading: isWorkAccessLevelsLoading } =
    useWorkAccessLevels();

  return {
    workTypes,
    estimateTypes,
    workStatuses,
    workAccessLevels,
    isWorkTypesLoading,
    isEstimateTypesLoading,
    isWorkStatusesLoading,
    isWorkAccessLevelsLoading,
  };
}

// Helper function to create default form values
export function getDefaultFormValues(
  orgId: string,
  parentId?: string | null,
): WorkFormValues {
  return {
    name: "",
    workTypeId: "",
    statusId: "",
    accessLevelId: null,
    tentativeStartDate: null,
    tentativeEndDate: null,
    actualStartDate: null,
    actualEndDate: null,
    tentativeBudget: null,
    approvedBudget: null,
    description: "",
    documentation: "",
    assignedTo: null,
    orgId: orgId,
    parentId: parentId || null,
  };
}

// Helper function to create a WorkModel from form values
export async function createWorkModelFromFormValues(
  formValues: WorkFormValues,
  orgId: string,
  parentId?: string | null,
  projectId?: string | null,
  existingWork?: WorkModel,
  productId?: string | null,
): Promise<WorkModel> {
  // Resolve effective parentId first
  const effectiveParentId = (() => {
    if (parentId !== undefined) return parentId;
    if (!formValues.parentId) return existingWork?.parentId || null;
    if (
      typeof formValues.parentId === "object" &&
      formValues.parentId !== null
    ) {
      return (formValues.parentId as Option).value;
    }
    return formValues.parentId;
  })();

  // No submit-time defaulting. Just honor form values or fall back to existing work, else null.
  const computedWorkTypeId: string | null =
    (formValues.workTypeId as string | null) ??
    existingWork?.workTypeId ??
    null;

  // Check if accessLevelId is a non-empty string
  const accessLevelId =
    formValues.accessLevelId &&
    typeof formValues.accessLevelId === "string" &&
    formValues.accessLevelId.length > 0
      ? formValues.accessLevelId
      : existingWork?.accessLevelId &&
          typeof existingWork.accessLevelId === "string" &&
          existingWork.accessLevelId.length > 0
        ? existingWork.accessLevelId
        : null;

  return {
    id: existingWork?.id,
    name: formValues.name,
    workTypeId: computedWorkTypeId?.length ? computedWorkTypeId : null,
    projectId: projectId || existingWork?.projectId || null,
    tentativeStartDate: formValues.tentativeStartDate
      ? new Date(formValues.tentativeStartDate).toISOString()
      : null,
    tentativeEndDate: formValues.tentativeEndDate
      ? new Date(formValues.tentativeEndDate).toISOString()
      : null,
    actualStartDate: formValues.actualStartDate
      ? new Date(formValues.actualStartDate).toISOString()
      : null,
    actualEndDate: formValues.actualEndDate
      ? new Date(formValues.actualEndDate).toISOString()
      : null,
    tentativeBudget: formValues.tentativeBudget,
    approvedBudget: formValues.approvedBudget,
    description: formValues.description?.length ? formValues.description : null,
    documentation:
      formValues.documentation ?? existingWork?.documentation ?? null,
    assignedTo:
      formValues.assignedTo && typeof formValues.assignedTo === "object"
        ? (formValues.assignedTo as Option).value
        : formValues.assignedTo,
    statusId: formValues.statusId?.length
      ? formValues.statusId
      : existingWork?.statusId?.length
        ? existingWork?.statusId
        : null,
    accessLevelId: accessLevelId,
    orgId: orgId,
    // Use the computed effective parentId
    parentId: effectiveParentId,
    productId: productId ?? existingWork?.productId ?? null,
  };
}
